import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCheckCircle,
  FaCopy,
  FaCogs,
  FaExclamationTriangle,
  FaEnvelope,
  FaKey,
  FaLink,
  FaPlus,
  FaPlug,
  FaRobot,
  FaSearchDollar,
  FaSync,
  FaTrash,
  FaUsers,
  FaUserGraduate,
  FaBoxes,
  FaBuilding,
  FaToggleOff,
  FaToggleOn,
} from "react-icons/fa";
import api from "../api";
import ProcessIntegrationSettings from "./ProcessIntegrationSettings";
import CentralNashIntegration from "./CentralNashIntegration";
import MailSettings from "./MailSettings";
import IntegrationLogs from "./IntegrationLogs";
import InfoTooltip from "../components/InfoTooltip";
import ProjectBankService from "../services/ProjectBankService";
import { useAuth } from "../AuthContext";
import { canManageNashAiIntegration } from "../utils/permissions";

const formatDateTime = (iso) => {
  if (!iso) {
    return "Nunca executado";
  }
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "Nunca executado";
    }
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  } catch (error) {
    return "Nunca executado";
  }
};

const automationTemplates = [
  {
    id: "http-webhook",
    title: "Webhook HTTP (genérico)",
    shortLabel: "Webhook REST",
    description:
      "Envie dados estruturados para qualquer endpoint REST compatível com JSON.",
    icon: FaPlug,
    accent: "bg-blue-100 text-blue-700",
    template: {
      nameSuffix: "Webhook REST",
      targetUrl: "https://api.seusistema.com/webhook",
      method: "POST",
      events: ["PROCESS_COMPLETED"],
      headers: { "Content-Type": "application/json" },
      timeoutMs: 15000,
    },
  },
  {
    id: "uipath",
    title: "UiPath Orchestrator",
    shortLabel: "UiPath",
    description:
      "Dispare jobs do UiPath com correlação automática de instâncias.",
    icon: FaRobot,
    accent: "bg-amber-100 text-amber-700",
    template: {
      nameSuffix: "UiPath Orchestrator",
      targetUrl:
        "https://orchestrator.seudominio.com/odata/Jobs/UiPath.Server.Configuration.OData.StartJobs",
      method: "POST",
      events: ["STEP_COMPLETED"],
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer {{token}}",
      },
      timeoutMs: 20000,
    },
  },
  {
    id: "price-lookup-rpa",
    title: "Robô RPA – Consulta de preços",
    shortLabel: "Busca de preços",
    description:
      "Acione o robô padrão que pesquisa valores em provedores online a partir de um campo do formulário.",
    icon: FaSearchDollar,
    accent: "bg-emerald-100 text-emerald-700",
    template: {
      nameSuffix: "Robô de preços RPA",
      targetUrl: "https://rpa.seudominio.com/robots/price-lookup/run",
      method: "POST",
      events: ["STEP_COMPLETED"],
      headers: { "Content-Type": "application/json" },
      timeoutMs: 30000,
    },
  },
  {
    id: "power-automate",
    title: "Power Automate (Flow)",
    shortLabel: "Power Automate",
    description:
      "Conecte um fluxo do Power Automate e receba a resposta em tempo real.",
    icon: FaCogs,
    accent: "bg-purple-100 text-purple-700",
    template: {
      nameSuffix: "Power Automate Flow",
      targetUrl: "https://prod-00.westeurope.logic.azure.com:443/...",
      method: "POST",
      events: ["PROCESS_COMPLETED"],
      headers: { "Content-Type": "application/json" },
      timeoutMs: 20000,
    },
  },
];

const automationHighlights = [
  {
    id: "templates",
    label: "Templates prontos",
    description:
      "Selecione o processo e aplique um template pré-configurado como RPA de preços, UiPath, Power Automate ou Webhook.",
  },
  {
    id: "monitoring",
    label: "Indicadores em tempo real",
    description:
      "Acompanhe tentativas, erros e tempo de resposta das integrações ativas.",
  },
  {
    id: "fallback",
    label: "Fallback orientado",
    description:
      "Informe quando o time operacional deve assumir a execução manual.",
  },
];

const automationTips = [
  {
    id: "payload",
    label: "Padronize o payload",
    description:
      "Valide entradas e saídas com os schemas publicados nas integrações para evitar erros em produção.",
  },
  {
    id: "monitoring",
    label: "Monitore execuções",
    description:
      "Habilite logs, métricas de tempo e alertas antes de liberar a automação para os usuários finais.",
  },
  {
    id: "fallback",
    label: "Defina fallback",
    description:
      "Planeje quando redirecionar para execução manual e como retomar a automação com segurança.",
  },
];

function UserProvisioningAutomationPanel() {
  const [sources, setSources] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotatingSourceId, setRotatingSourceId] = useState(null);
  const [revealedToken, setRevealedToken] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    sourceKey: "",
    passwordPattern: "CPF6",
    fixedPassword: "",
    defaultGroupId: "",
    defaultSectorId: "",
    loginUrl: "",
    sendAccessEmail: true,
  });

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [sourcesResponse, groupsResponse, sectorsResponse] =
        await Promise.all([
          api.get("/users/provisioning/sources"),
          api.get("/groups"),
          api.get("/sectors"),
        ]);
      setSources(
        Array.isArray(sourcesResponse.data) ? sourcesResponse.data : [],
      );
      setGroups(Array.isArray(groupsResponse.data) ? groupsResponse.data : []);
      setSectors(
        Array.isArray(sectorsResponse.data) ? sectorsResponse.data : [],
      );
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        "Não foi possível carregar a automação de cadastro de usuários.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearForm = () => {
    setForm({
      name: "",
      sourceKey: "",
      passwordPattern: "CPF6",
      fixedPassword: "",
      defaultGroupId: "",
      defaultSectorId: "",
      loginUrl: "",
      sendAccessEmail: true,
    });
  };

  const handleCreateSource = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFeedback("");
    try {
      const payload = {
        name: form.name,
        sourceKey: form.sourceKey || form.name,
        passwordPattern: form.passwordPattern,
        fixedPassword:
          form.passwordPattern === "FIXED" ? form.fixedPassword : undefined,
        defaultGroupId: form.defaultGroupId
          ? Number(form.defaultGroupId)
          : undefined,
        defaultSectorId: form.defaultSectorId
          ? Number(form.defaultSectorId)
          : undefined,
        loginUrl: form.loginUrl || undefined,
        sendAccessEmail: Boolean(form.sendAccessEmail),
      };
      const { data } = await api.post("/users/provisioning/sources", payload);
      if (data?.token) {
        setRevealedToken({
          sourceName: data.name || form.name,
          token: data.token,
        });
        setFeedback("Fonte criada com sucesso. Copie o token antes de ocultá-lo.");
      } else {
        setFeedback(
          "Fonte criada, mas o token não foi retornado. Gere um novo token antes de integrar.",
        );
      }
      clearForm();
      await loadData();
    } catch (err) {
      const message =
        err?.response?.data?.error || "Erro ao criar fonte de integração";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    const confirmed = window.confirm(
      "Deseja remover esta fonte de integração?",
    );
    if (!confirmed) return;
    setError("");
    setFeedback("");
    try {
      await api.delete(`/users/provisioning/sources/${sourceId}`);
      setFeedback("Fonte removida com sucesso.");
      await loadData();
    } catch (err) {
      const message = err?.response?.data?.error || "Erro ao remover fonte";
      setError(message);
    }
  };

  const handleToggleActive = async (source) => {
    setError("");
    setFeedback("");
    try {
      await api.put(`/users/provisioning/sources/${source.id}`, {
        active: !source.active,
      });
      setFeedback(
        `Fonte ${!source.active ? "ativada" : "desativada"} com sucesso.`,
      );
      await loadData();
    } catch (err) {
      const message =
        err?.response?.data?.error || "Erro ao atualizar status da fonte";
      setError(message);
    }
  };

  const handleRotateToken = async (source) => {
    const confirmed = window.confirm(
      `Gerar um novo token para "${source.name}"? O token atual deixará de funcionar imediatamente.`,
    );
    if (!confirmed) return;

    setRotatingSourceId(source.id);
    setError("");
    setFeedback("");
    setRevealedToken(null);
    try {
      const { data } = await api.post(
        `/users/provisioning/sources/${source.id}/rotate-token`,
      );
      if (!data?.token) {
        throw new Error("A API não retornou o novo token");
      }
      setRevealedToken({
        sourceName: data.name || source.name,
        token: data.token,
      });
      setFeedback("Token renovado. Atualize a integração externa agora.");
      await loadData();
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao gerar um novo token";
      setError(message);
    } finally {
      setRotatingSourceId(null);
    }
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado com sucesso.`);
    } catch (err) {
      setError(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const resolveIntakeUrl = (source) => {
    const base = String(
      api?.defaults?.baseURL || window.location.origin,
    ).replace(/\/$/, "");
    const path =
      source?.intakePath ||
      `/users/provisioning/intake/${source?.sourceKey || ""}`;
    return `${base}${path}`;
  };

  const payloadExample = useMemo(() => {
    const selectedGroupId = form.defaultGroupId
      ? Number(form.defaultGroupId)
      : null;
    const selectedSectorId = form.defaultSectorId
      ? Number(form.defaultSectorId)
      : null;

    const examplePayload = {
      users: [
        {
          name: "Nome da Pessoa",
          email: "usuario@empresa.com",
          cpf: "12345678900",
          groupId: Number.isFinite(selectedGroupId) ? selectedGroupId : null,
          sectorId: Number.isFinite(selectedSectorId) ? selectedSectorId : null,
        },
      ],
    };

    return JSON.stringify(examplePayload, null, 2);
  }, [form.defaultGroupId, form.defaultSectorId]);

  return (
    <section className="space-y-5 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
      <header className="flex flex-col gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <FaUsers className="text-blue-600" /> Automação de Cadastro de Usuário
        </h2>
        <p className="text-sm text-slate-600">
          Configure fontes externas para o sistema gerar automaticamente a API
          de intake. Depois, basta colar o link no outro sistema e enviar os
          usuários no padrão abaixo.
        </p>
      </header>

      {feedback ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {feedback}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {revealedToken ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="font-semibold">
                Token de {revealedToken.sourceName} — exibição única
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Copie e guarde em local seguro. Depois que esta mensagem for
                ocultada ou a página for atualizada, o token não poderá ser
                consultado novamente.
              </p>
              <code className="mt-3 block break-all rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs">
                {revealedToken.token}
              </code>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(revealedToken.token, "Token")
                }
                className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700"
              >
                <FaCopy /> Copiar token
              </button>
              <button
                type="button"
                onClick={() => setRevealedToken(null)}
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-800"
              >
                Ocultar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={handleCreateSource}
        className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-2"
      >
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Nome da fonte
          </label>
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            required
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Ex.: ERP RH Matriz"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Chave da fonte
          </label>
          <input
            value={form.sourceKey}
            onChange={(e) => setField("sourceKey", e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Opcional (se vazio, usa o nome)"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Padrão de senha
          </label>
          <select
            value={form.passwordPattern}
            onChange={(e) => setField("passwordPattern", e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="CPF6">6 primeiros dígitos do CPF</option>
            <option value="FIXED">Senha fixa</option>
          </select>
        </div>
        {form.passwordPattern === "FIXED" ? (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Senha fixa
            </label>
            <input
              value={form.fixedPassword}
              onChange={(e) => setField("fixedPassword", e.target.value)}
              required
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        ) : null}
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Grupo padrão (opcional)
          </label>
          <select
            value={form.defaultGroupId}
            onChange={(e) => setField("defaultGroupId", e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Sem grupo padrão</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Setor padrão (opcional)
          </label>
          <select
            value={form.defaultSectorId}
            onChange={(e) => setField("defaultSectorId", e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Sem setor padrão</option>
            {sectors.map((sector) => (
              <option key={sector.id} value={sector.id}>
                {sector.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Link de login (opcional)
          </label>
          <input
            value={form.loginUrl}
            onChange={(e) => setField("loginUrl", e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="https://seu-dominio.com/login"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
          <input
            type="checkbox"
            checked={form.sendAccessEmail}
            onChange={(e) => setField("sendAccessEmail", e.target.checked)}
          />
          Enviar e-mail automático com login/senha/link após cadastro
        </label>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70"
          >
            <FaPlus /> {saving ? "Salvando..." : "Criar fonte e gerar API"}
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
          Como configurar no outro sistema
        </h3>
        <ul className="list-disc space-y-1 pl-5">
          {form.direction === "inbound" ||
          form.direction === "bidirectional" ? (
            <li>
              Para receber dados aqui, configure no outro sistema uma chamada
              HTTP POST para o link gerado na fonte, envie o token em{" "}
              <code>x-provisioning-token</code> ou{" "}
              <code>Authorization: Bearer TOKEN</code> e use o payload abaixo.
            </li>
          ) : null}
          {form.direction === "outbound" ||
          form.direction === "bidirectional" ? (
            <li>
              Para enviar dados para fora, use os mesmos campos selecionados
              como contrato de saída no conector/webhook do outro sistema; copie
              o payload abaixo como modelo de mapeamento.
            </li>
          ) : null}
          <li>
            Campos obrigatórios aparecem bloqueados e precisam existir no
            payload para o cadastro ser aceito.
          </li>
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
          Payload padrão
        </h3>
        <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100">
          {payloadExample}
        </pre>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
          Fontes configuradas
        </h3>
        {loading ? (
          <div className="text-sm text-slate-500">Carregando fontes...</div>
        ) : sources.length ? (
          <div className="space-y-3">
            {sources.map((source) => {
              const intakeUrl = resolveIntakeUrl(source);
              return (
                <article
                  key={source.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="text-base font-semibold text-slate-900">
                        {source.name}
                      </h4>
                      <p className="text-xs text-slate-500">
                        sourceKey: {source.sourceKey}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(source)}
                        className={`rounded-lg px-3 py-1 text-xs font-semibold ${source.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}
                      >
                        {source.active ? "Ativa" : "Inativa"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSource(source.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700"
                      >
                        <FaTrash /> Remover
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="mb-1 font-semibold text-slate-500">
                        API / Link de envio
                      </p>
                      <p className="break-all">{intakeUrl}</p>
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(intakeUrl, "Link de API")
                        }
                        className="mt-2 inline-flex items-center gap-1 text-blue-600"
                      >
                        <FaLink /> <FaCopy /> Copiar link
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="mb-1 font-semibold text-slate-500">
                        Token de autenticação
                      </p>
                      <p>
                        {source.tokenConfigured
                          ? "Protegido e não recuperável"
                          : "Token ainda não configurado"}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRotateToken(source)}
                        disabled={rotatingSourceId === source.id}
                        className="mt-2 inline-flex items-center gap-1 text-blue-600 disabled:opacity-60"
                      >
                        {rotatingSourceId === source.id ? (
                          <FaSync className="animate-spin" />
                        ) : (
                          <FaKey />
                        )}
                        {rotatingSourceId === source.id
                          ? "Gerando..."
                          : "Gerar novo token"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            Nenhuma fonte criada ainda. Cadastre uma fonte para gerar o endpoint
            de integração.
          </div>
        )}
      </div>
    </section>
  );
}

export function ResourceProvisioningPanel({
  title,
  description,
  icon: Icon,
  sourceBasePath,
  defaultPath,
  payloadExample,
  fields = [],
  payloadRoot = null,
}) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [revealedToken, setRevealedToken] = useState(null);
  const [togglingSourceId, setTogglingSourceId] = useState(null);
  const defaultFieldKeys = fields
    .filter((field) => field.required || field.default)
    .map((field) => field.key);
  const [form, setForm] = useState({
    name: "",
    sourceKey: "",
    token: "",
    fields: defaultFieldKeys,
    direction: "inbound",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`${sourceBasePath}/sources`);
      setSources(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err?.response?.data?.error || "Não foi possível carregar as fontes.",
      );
    } finally {
      setLoading(false);
    }
  }, [sourceBasePath]);

  useEffect(() => {
    setRevealedToken(null);
    loadData();
  }, [loadData]);

  const resolveIntakeUrl = (source) => {
    const base = String(
      api?.defaults?.baseURL || window.location.origin,
    ).replace(/\/$/, "");
    const path =
      source?.intakePath || `${defaultPath}/${source?.sourceKey || ""}`;
    return `${base}${path}`;
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado com sucesso.`);
    } catch (err) {
      setError(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const handleCreateSource = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFeedback("");
    try {
      const { data } = await api.post(`${sourceBasePath}/sources`, {
        name: form.name,
        sourceKey: form.sourceKey || form.name,
        token: form.token || undefined,
        fields: form.fields,
        direction: form.direction,
      });
      setRevealedToken(
        data?.token ? { sourceId: data.id, token: data.token } : null,
      );
      setFeedback("Fonte criada com sucesso. Copie o token exibido uma única vez.");
      setForm({
        name: "",
        sourceKey: "",
        token: "",
        fields: defaultFieldKeys,
        direction: "inbound",
      });
      await loadData();
    } catch (err) {
      setError(
        err?.response?.data?.error || "Erro ao criar fonte de integração.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (source) => {
    if (togglingSourceId !== null) return;
    setTogglingSourceId(source.id);
    setError("");
    setFeedback("");
    try {
      await api.put(`${sourceBasePath}/sources/${source.id}`, {
        active: !source.active,
      });
      setFeedback(
        `Integração ${!source.active ? "habilitada" : "desabilitada"} com sucesso.`,
      );
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Erro ao atualizar a integração.");
    } finally {
      setTogglingSourceId(null);
    }
  };

  const handleComplementOnly = async (source) => {
    if (togglingSourceId !== null) return;
    setTogglingSourceId(source.id);
    setError("");
    try {
      await api.put(`${sourceBasePath}/sources/${source.id}`, { syncMode: "complement-only" });
      setFeedback("Somente complementação habilitada. No menu do Conere, abra Complementar dados do iBPM e escolha uma etapa.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível preparar a complementação.");
    } finally { setTogglingSourceId(null); }
  };

  const handleRotateToken = async (source) => {
    if (
      !window.confirm(
        "Gerar um novo token? O token anterior deixará de funcionar imediatamente.",
      )
    ) {
      return;
    }
    try {
      setError("");
      const { data } = await api.post(
        `${sourceBasePath}/sources/${source.id}/rotate-token`,
      );
      setRevealedToken(
        data?.token ? { sourceId: source.id, token: data.token } : null,
      );
      setFeedback(
        "Novo token gerado. Copie-o agora; ele não será exibido novamente.",
      );
      await loadData();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Não foi possível gerar um novo token.",
      );
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm("Deseja remover esta fonte de integração?")) return;
    try {
      await api.delete(`${sourceBasePath}/sources/${sourceId}`);
      setFeedback("Fonte removida com sucesso.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Erro ao remover fonte.");
    }
  };

  const toggleField = (fieldKey, checked) => {
    setForm((prev) => ({
      ...prev,
      fields: checked
        ? Array.from(new Set([...(prev.fields || []), fieldKey]))
        : (prev.fields || []).filter((item) => item !== fieldKey),
    }));
  };

  const groupedFields = fields.reduce((groups, field) => {
    const category = field.category || "Campos";
    return { ...groups, [category]: [...(groups[category] || []), field] };
  }, {});

  const setPayloadValue = (target, path, value) => {
    const parts = String(path || "")
      .split(".")
      .filter(Boolean);
    if (!parts.length) return target;
    const next = { ...target };
    let cursor = next;
    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        cursor[part] = value;
        return;
      }
      cursor[part] =
        cursor[part] &&
        typeof cursor[part] === "object" &&
        !Array.isArray(cursor[part])
          ? { ...cursor[part] }
          : {};
      cursor = cursor[part];
    });
    return next;
  };

  const buildPayloadFromFields = (selectedFields = form.fields) => {
    if (!fields.length || !payloadRoot) return payloadExample;
    const row = fields
      .filter((field) => selectedFields.includes(field.key))
      .reduce(
        (acc, field) =>
          setPayloadValue(acc, field.payloadKey || field.key, field.example),
        {},
      );
    return JSON.stringify({ [payloadRoot]: [row] }, null, 2);
  };

  const selectedPayloadExample = buildPayloadFromFields();

  return (
    <section className="space-y-5 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
      <header className="flex flex-col gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Icon className="text-blue-600" /> {title}
        </h2>
        <p className="text-sm text-slate-600">{description}</p>
      </header>
      {feedback ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {feedback}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <form
        onSubmit={handleCreateSource}
        className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-3"
      >
        <input
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
          required
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Nome da fonte"
        />
        <input
          value={form.sourceKey}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, sourceKey: e.target.value }))
          }
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Chave da fonte (opcional)"
        />
        <input
          value={form.token}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, token: e.target.value }))
          }
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Token (opcional)"
        />
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 md:col-span-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Tipo de integração
          </p>
          <div className="grid gap-2 md:grid-cols-3">
            {[
              {
                value: "inbound",
                label: "Receber dados",
                description:
                  "O outro sistema envia dados para esta plataforma.",
              },
              {
                value: "outbound",
                label: "Enviar dados",
                description:
                  "Esta plataforma envia/expõe dados para o outro sistema consumir.",
              },
              {
                value: "bidirectional",
                label: "Mão dupla",
                description:
                  "Use o envio e o recebimento com o mesmo contrato de campos.",
              },
            ].map((option) => (
              <label
                key={option.value}
                className={`rounded-lg border px-3 py-2 ${form.direction === option.value ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-slate-50"}`}
              >
                <input
                  type="radio"
                  className="mr-2"
                  name="provisioning-direction"
                  value={option.value}
                  checked={form.direction === option.value}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, direction: e.target.value }))
                  }
                />
                <strong>{option.label}</strong>
                <span className="mt-1 block text-xs text-slate-500">
                  {option.description}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70"
          >
            <FaPlus /> {saving ? "Salvando..." : "Criar fonte e gerar API"}
          </button>
        </div>
      </form>

      {fields.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
            Campos para preencher no outro sistema
          </h3>
          <div className="max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/60 p-2">
            <div className="space-y-4">
              {Object.entries(groupedFields).map(
                ([category, categoryFields]) => (
                  <div key={category}>
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {category}
                    </h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {categoryFields.map((field) => (
                        <label
                          key={field.key}
                          className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={(form.fields || []).includes(field.key)}
                            disabled={field.required}
                            onChange={(e) =>
                              toggleField(field.key, e.target.checked)
                            }
                          />
                          <span>
                            <strong>{field.label}</strong>
                            {field.required ? (
                              <span className="ml-1 text-xs text-blue-600">
                                obrigatório
                              </span>
                            ) : null}
                            <span className="block text-xs text-slate-500">
                              {field.payloadKey || field.key}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
          Como configurar no outro sistema
        </h3>
        <ul className="list-disc space-y-1 pl-5">
          {form.direction === "inbound" ||
          form.direction === "bidirectional" ? (
            <li>
              Para receber dados aqui, configure no outro sistema uma chamada
              HTTP POST para o link gerado na fonte, envie o token em{" "}
              <code>x-provisioning-token</code> ou{" "}
              <code>Authorization: Bearer TOKEN</code> e use o payload abaixo.
            </li>
          ) : null}
          {form.direction === "outbound" ||
          form.direction === "bidirectional" ? (
            <li>
              Para enviar dados para fora, use os mesmos campos selecionados
              como contrato de saída no conector/webhook do outro sistema; copie
              o payload abaixo como modelo de mapeamento.
            </li>
          ) : null}
          <li>
            Campos obrigatórios aparecem bloqueados e precisam existir no
            payload para o cadastro ser aceito.
          </li>
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
          Payload padrão
        </h3>
        <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100">
          {selectedPayloadExample}
        </pre>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Carregando fontes...</div>
      ) : (
        <div className="space-y-3">
          {sources.map((source) => {
            const intakeUrl = resolveIntakeUrl(source);
            const visibleToken =
              revealedToken?.sourceId === source.id
                ? revealedToken.token
                : "";
            return (
              <article
                key={source.id}
                className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-base font-semibold text-slate-900">
                      {source.name}
                    </h4>
                    <p className="text-xs text-slate-500">
                      sourceKey: {source.sourceKey}
                    </p>
                    <p className="text-xs text-slate-500">
                      Tipo:{" "}
                      {{
                        inbound: "Receber dados",
                        outbound: "Enviar dados",
                        bidirectional: "Mão dupla",
                      }[source.direction] || "Receber dados"}
                    </p>
                    {sourceBasePath === "/students/provisioning" && source.syncMode === "complement-only" ? (
                      <p className="text-xs font-semibold text-blue-700">Somente complementação — sincronização automática pausada</p>
                    ) : null}
                    {Array.isArray(source.fields) && source.fields.length ? (
                      <p className="text-xs text-slate-500">
                        Campos: {source.fields.join(", ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-lg px-3 py-1 text-xs font-semibold ${source.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}
                      aria-label={`Status da integração: ${source.active ? "Ativa" : "Inativa"}`}
                    >
                      {source.active ? "Ativa" : "Inativa"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(source)}
                      disabled={togglingSourceId !== null}
                      className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${source.active ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                    >
                      {source.active ? <FaToggleOff /> : <FaToggleOn />}
                      {togglingSourceId === source.id
                        ? "Atualizando..."
                        : source.active
                          ? "Desabilitar integração"
                          : "Habilitar integração"}
                    </button>
                    {sourceBasePath === "/students/provisioning" && source.active && source.syncMode !== "complement-only" ? (
                      <button type="button" onClick={() => handleComplementOnly(source)} disabled={togglingSourceId !== null}
                        className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 disabled:opacity-60">
                        Somente complementar dados
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleDeleteSource(source.id)}
                      className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700"
                    >
                      <FaTrash /> Remover
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="mb-1 font-semibold text-slate-500">
                      API / Link de envio
                    </p>
                    <p className="break-all">{intakeUrl}</p>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(intakeUrl, "Link de API")}
                      className="mt-2 inline-flex items-center gap-1 text-blue-600"
                    >
                      <FaLink /> <FaCopy /> Copiar link
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="mb-1 font-semibold text-slate-500">
                      Token de autenticação
                    </p>
                    <p className="break-all">
                      {visibleToken ||
                        (source.tokenConfigured
                          ? "Token protegido — gere um novo somente se precisar copiá-lo."
                          : "Sem token configurado")}
                    </p>
                    {visibleToken ? (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(visibleToken, "Token")}
                        className="mt-2 inline-flex items-center gap-1 text-blue-600"
                      >
                        <FaKey /> <FaCopy /> Copiar token
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleRotateToken(source)}
                      className="mt-2 ml-3 inline-flex items-center gap-1 text-amber-700"
                    >
                      <FaKey /> Gerar novo token
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
          {!sources.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Nenhuma fonte criada ainda. Cadastre uma fonte para gerar o
              endpoint de integração.
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

export default function AutomationHub({ openTab }) {
  const { user } = useAuth();
  const canManageNashAiSettings = canManageNashAiIntegration(user);
  const [automationView, setAutomationView] = useState("process");
  const [summary, setSummary] = useState([]);
  const [centralScope, setCentralScope] = useState(null);
  useEffect(()=>{let live=true;api.get('/processes/central-nash/connection').then(({data})=>{if(live)setCentralScope(data.connected?data.scope:null);}).catch(()=>{});return()=>{live=false;};},[]);
  const integrationSummary = useMemo(()=>summary.map(item=>{
    const connected=centralScope&&['clients','invoices','tickets'].some(role=>centralScope[role]===item.processId);
    return connected?{...item,totalIntegrations:item.totalIntegrations+1,activeIntegrations:item.activeIntegrations+1,centralNash:true,lastIntegrationUpdate:centralScope.createdAt}:item;
  }),[summary,centralScope]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [aiSettings, setAiSettings] = useState({
    openAiApiKey: "",
    openAiModel: "gpt-5.4-mini",
    openAiApiKeyConfigured: false,
    openAiApiKeySource: null,
    clearOpenAiApiKey: false,
  });
  const [nashAiSettings, setNashAiSettings] = useState({
    nashOpenAiApiKey: "",
    nashOpenAiModel: "gpt-5.4-mini",
    nashOpenAiApiKeyConfigured: false,
    nashOpenAiApiKeySource: null,
    clearNashOpenAiApiKey: false,
  });
  const [aiSettingsLoading, setAiSettingsLoading] = useState(true);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [aiSettingsMessage, setAiSettingsMessage] = useState("");
  const [aiSettingsError, setAiSettingsError] = useState("");
  const [nashAiSettingsLoading, setNashAiSettingsLoading] = useState(true);
  const [nashAiSettingsSaving, setNashAiSettingsSaving] = useState(false);
  const [nashAiSettingsMessage, setNashAiSettingsMessage] = useState("");
  const [nashAiSettingsError, setNashAiSettingsError] = useState("");

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/processes/integrations/summary");
        if (!isMounted) return;
        setSummary(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        const message =
          err?.response?.data?.error ||
          "Não foi possível carregar as automações.";
        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!canManageNashAiSettings) {
      setNashAiSettingsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const loadNashAiSettings = async () => {
      setNashAiSettingsLoading(true);
      setNashAiSettingsError("");
      try {
        const settings =
          await ProjectBankService.getNashAIIntegrationSettings();
        if (!isMounted) return;
        setNashAiSettings((prev) => ({
          ...prev,
          ...settings,
          nashOpenAiApiKey: "",
          clearNashOpenAiApiKey: false,
        }));
      } catch (err) {
        if (!isMounted) return;
        setNashAiSettingsError(
          err?.response?.data?.error ||
            "Não foi possível carregar a integração ChatGPT da Nash IA.",
        );
      } finally {
        if (isMounted) setNashAiSettingsLoading(false);
      }
    };
    loadNashAiSettings();
    return () => {
      isMounted = false;
    };
  }, [canManageNashAiSettings]);

  useEffect(() => {
    let isMounted = true;
    const loadAiSettings = async () => {
      setAiSettingsLoading(true);
      setAiSettingsError("");
      try {
        const settings = await ProjectBankService.getAIIntegrationSettings();
        if (!isMounted) return;
        setAiSettings((prev) => ({
          ...prev,
          ...settings,
          openAiApiKey: "",
          clearOpenAiApiKey: false,
        }));
      } catch (err) {
        if (!isMounted) return;
        setAiSettingsError(
          err?.response?.data?.error ||
            "Não foi possível carregar a integração com ChatGPT.",
        );
      } finally {
        if (isMounted) setAiSettingsLoading(false);
      }
    };
    loadAiSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  const saveAiIntegrationSettings = async (event) => {
    event.preventDefault();
    setAiSettingsSaving(true);
    setAiSettingsMessage("");
    setAiSettingsError("");
    try {
      const saved =
        await ProjectBankService.saveAIIntegrationSettings(aiSettings);
      setAiSettings((prev) => ({
        ...prev,
        ...saved,
        openAiApiKey: "",
        clearOpenAiApiKey: false,
      }));
      setAiSettingsMessage("Integração com ChatGPT salva com sucesso.");
    } catch (err) {
      setAiSettingsError(
        err?.response?.data?.error ||
          "Não foi possível salvar a integração com ChatGPT.",
      );
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const updateAiSetting = (field, value) => {
    setAiSettingsMessage("");
    setAiSettingsError("");
    setAiSettings((prev) => ({ ...prev, [field]: value }));
  };

  const saveNashAiIntegrationSettings = async (event) => {
    event.preventDefault();
    setNashAiSettingsSaving(true);
    setNashAiSettingsMessage("");
    setNashAiSettingsError("");
    try {
      const saved =
        await ProjectBankService.saveNashAIIntegrationSettings(nashAiSettings);
      setNashAiSettings((prev) => ({
        ...prev,
        ...saved,
        nashOpenAiApiKey: "",
        clearNashOpenAiApiKey: false,
      }));
      setNashAiSettingsMessage(
        "Integração ChatGPT da Nash IA salva com sucesso.",
      );
    } catch (err) {
      setNashAiSettingsError(
        err?.response?.data?.error ||
          "Não foi possível salvar a integração ChatGPT da Nash IA.",
      );
    } finally {
      setNashAiSettingsSaving(false);
    }
  };

  const updateNashAiSetting = (field, value) => {
    setNashAiSettingsMessage("");
    setNashAiSettingsError("");
    setNashAiSettings((prev) => ({ ...prev, [field]: value }));
  };

  const metrics = useMemo(() => {
    const totalProcesses = integrationSummary.length;
    const withAutomation = integrationSummary.filter(
      (item) => item.totalIntegrations > 0,
    ).length;
    const adoptionRate = totalProcesses
      ? Math.round((withAutomation / totalProcesses) * 100)
      : 0;
    const totalActive = integrationSummary.reduce(
      (acc, item) => acc + Number(item.activeIntegrations || 0),
      0,
    );
    const pending = integrationSummary.reduce(
      (acc, item) => acc + Number(item.inactiveIntegrations || 0),
      0,
    );
    return {
      totalProcesses,
      withAutomation,
      adoptionRate,
      totalActive,
      pending,
    };
  }, [integrationSummary]);

  const filteredProcesses = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return integrationSummary.filter((process) => {
      const matchesSearch = !normalizedSearch
        ? true
        : [process.processName, process.category]
            .filter(Boolean)
            .some((value) =>
              String(value).toLowerCase().includes(normalizedSearch),
            );

      const hasAutomations = process.totalIntegrations > 0;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "with" && hasAutomations) ||
        (statusFilter === "without" && !hasAutomations);

      return matchesSearch && matchesStatus;
    });
  }, [integrationSummary, searchTerm, statusFilter]);

  const openSettings = (processItem, templateDef = null) => {
    if (!openTab) return;
    const title = processItem.processName
      ? `Integrações: ${processItem.processName}`
      : `Integrações: Processo #${processItem.processId}`;
    const tabProps = {
      processId: processItem.processId,
      processName: processItem.processName,
    };

    if (templateDef) {
      const baseName = processItem.processName
        ? processItem.processName
        : `Processo ${processItem.processId}`;
      const nameSuffix =
        templateDef.template?.nameSuffix || templateDef.shortLabel;
      const payload = {
        name: `${baseName} - ${nameSuffix}`,
        targetUrl:
          typeof templateDef.template?.targetUrl === "string"
            ? templateDef.template.targetUrl
            : "",
        method: templateDef.template?.method || "POST",
        events: Array.isArray(templateDef.template?.events)
          ? templateDef.template.events
          : ["PROCESS_COMPLETED"],
        headers: templateDef.template?.headers || null,
        timeoutMs: templateDef.template?.timeoutMs || 15000,
        active: true,
      };
      tabProps.template = payload;
    }

    openTab(
      `process-${processItem.processId}-integrations`,
      title,
      ProcessIntegrationSettings,
      tabProps,
    );
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-[#071f54] p-5 text-white shadow-xl md:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-blue-100">
              <FaPlug /> Integrações
            </span>
            <div className="mt-3 flex items-center gap-2">
              <h1 className="text-[1.45rem] font-black leading-tight">Central de Integrações</h1>
              <InfoTooltip label="Conecte processos, APIs, RPA, cadastros e recursos da Nash IA em um único lugar." />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-extrabold">
            <span className="rounded-full bg-white/10 px-3 py-2">{metrics.totalProcesses.toLocaleString("pt-BR")} processos</span>
            <span className="rounded-full bg-white/10 px-3 py-2">{metrics.totalActive.toLocaleString("pt-BR")} ativas</span>
            <span className="rounded-full bg-white px-3 py-2 text-[#071f54]">{metrics.adoptionRate}% automatizados</span>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200/70 bg-white/90 p-2 shadow-sm">
        <div className="flex max-w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
          <button
            type="button"
            onClick={() => setAutomationView("process")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "process"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Processos
          </button>
          <button type="button" onClick={()=>setAutomationView('central-nash')} className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium ${automationView==='central-nash'?'bg-blue-600 text-white':'bg-slate-100 text-slate-700'}`}>Central Nash</button>
          <button
            type="button"
            onClick={() => setAutomationView("mail")}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "mail"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            <FaEnvelope /> E-mail
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("logs")}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "logs"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            <FaExclamationTriangle /> Logs
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("user-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "user-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Usuários
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("inventory-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "inventory-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Estoque
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("asset-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "asset-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Patrimônio
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("students-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "students-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Alunos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("student-classes-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "student-classes-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Turmas de Alunos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("student-courses-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "student-courses-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Cursos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("attendees-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "attendees-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Atendidos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("attendee-classes-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "attendee-classes-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Turmas de Atendidos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView("attendee-groups-provisioning")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "attendee-groups-provisioning"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Grupos de Atendidos
          </button>
          {canManageNashAiSettings ? (
            <button
              type="button"
              onClick={() => setAutomationView("nash-chatgpt")}
              className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
                automationView === "nash-chatgpt"
                  ? "bg-blue-600 text-white shadow"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              ChatGPT da Nash IA
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setAutomationView("project-bank-ai")}
            className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === "project-bank-ai"
                ? "bg-blue-600 text-white shadow"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            IA de Projetos e Estoque
          </button>
        </div>
      </section>

      {automationView === "mail" ? <MailSettings /> : null}
      {automationView === "logs" ? <IntegrationLogs /> : null}

      {automationView === 'central-nash' && <CentralNashIntegration processes={summary} onChange={setCentralScope}/>}
      {automationView === "user-provisioning" ? (
        <UserProvisioningAutomationPanel />
      ) : null}
      {automationView === "inventory-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Estoque"
          description="Crie fontes de integração para cadastrar itens de estoque via API externa."
          icon={FaBoxes}
          sourceBasePath="/inventory/provisioning"
          defaultPath="/inventory/provisioning/intake"
          payloadExample={`{\n  "items": [\n    {\n      "name": "Cabo de Rede Cat6",\n      "sku": "CB-CAT6-10M",\n      "warehouseId": 1,\n      "categoryId": 1,\n      "stock": 150\n    }\n  ]\n}`}
        />
      ) : null}
      {automationView === "asset-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Patrimônio"
          description="Crie fontes de integração para importar ativos patrimoniais de sistemas externos."
          icon={FaBuilding}
          sourceBasePath="/assets/provisioning"
          defaultPath="/assets/provisioning/intake"
          payloadExample={`{\n  "assets": [\n    {\n      "name": "Notebook Dell Latitude",\n      "tag": "PAT-2044",\n      "serialNumber": "DL-99887",\n      "status": "OPERATING",\n      "categoryName": "TI",\n      "locationName": "Sede"\n    }\n  ]\n}`}
        />
      ) : null}

      {automationView === "students-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Alunos"
          description="Escolha os campos do cadastro de alunos que o outro sistema deve enviar e gere o payload para colar na aplicação externa."
          icon={FaUserGraduate}
          sourceBasePath="/students/provisioning"
          defaultPath="/students/provisioning/intake"
          payloadRoot="students"
          fields={[
            {
              category: "Dados pessoais",
              key: "registrationNumber",
              label: "Matrícula",
              example: "2026-001",
            },
            {
              category: "Dados pessoais",
              key: "photoUrl",
              label: "Foto",
              example: "https://cdn.exemplo.com/alunos/maria.jpg",
            },
            {
              category: "Dados pessoais",
              key: "fullName",
              label: "Nome completo",
              example: "Maria da Silva",
              required: true,
            },
            {
              category: "Dados pessoais",
              key: "socialName",
              label: "Nome social",
              example: "Maria",
            },
            {
              category: "Dados pessoais",
              key: "enrollmentDate",
              label: "Data de inscrição",
              example: "2026-02-01",
              default: true,
            },
            {
              category: "Dados pessoais",
              key: "birthDate",
              label: "Data de nascimento",
              example: "2010-05-20",
            },
            {
              category: "Dados pessoais",
              key: "gender",
              label: "Gênero",
              example: "Feminino",
            },
            {
              category: "Dados pessoais",
              key: "ethnicity",
              label: "Etnia/cor",
              example: "Parda",
            },
            {
              category: "Dados pessoais",
              key: "nationality",
              label: "Nacionalidade",
              example: "Brasileira",
            },
            {
              category: "Dados pessoais",
              key: "nis",
              label: "NIS",
              example: "12345678901",
            },
            {
              category: "Contato e documentos",
              key: "whatsapp",
              label: "WhatsApp",
              example: "(11) 99999-0000",
            },
            {
              category: "Contato e documentos",
              key: "phone",
              label: "Telefone",
              example: "(11) 3000-0000",
            },
            {
              category: "Contato e documentos",
              key: "email",
              label: "E-mail",
              example: "maria@exemplo.com",
              default: true,
            },
            {
              category: "Contato e documentos",
              key: "cpf",
              label: "CPF",
              example: "12345678900",
              required: true,
            },
            {
              category: "Contato e documentos",
              key: "rg",
              label: "RG",
              example: "12.345.678-9",
            },
            {
              category: "Curso e status",
              key: "exitDate",
              label: "Data de saída",
              example: "2026-12-20",
            },
            {
              category: "Curso e status",
              key: "exitReason",
              label: "Motivo de saída",
              example: "Conclusão do curso",
            },
            {
              category: "Curso e status",
              key: "status",
              label: "Status",
              example: "ACTIVE",
              default: true,
            },
            {
              category: "Curso e status",
              key: "classId",
              label: "ID da turma",
              example: 1,
              default: true,
            },
            {
              category: "Curso e status",
              key: "classLinkedAt",
              label: "Data de vínculo com turma",
              example: "2026-02-01",
            },
            {
              category: "Família",
              key: "family.responsible1.name",
              label: "Responsável 1 - nome",
              example: "João da Silva",
            },
            {
              category: "Família",
              key: "family.responsible1.cpf",
              label: "Responsável 1 - CPF",
              example: "98765432100",
            },
            {
              category: "Família",
              key: "family.responsible1.whatsapp",
              label: "Responsável 1 - WhatsApp",
              example: "(11) 98888-0000",
            },
            {
              category: "Família",
              key: "family.responsible1.livesWithStudent",
              label: "Responsável 1 reside com aluno",
              example: true,
            },
            {
              category: "Família",
              key: "family.responsible2.name",
              label: "Responsável 2 - nome",
              example: "Ana da Silva",
            },
            {
              category: "Família",
              key: "family.responsible2.cpf",
              label: "Responsável 2 - CPF",
              example: "11122233344",
            },
            {
              category: "Família",
              key: "family.responsible2.whatsapp",
              label: "Responsável 2 - WhatsApp",
              example: "(11) 97777-0000",
            },
            {
              category: "Família",
              key: "family.responsible2.livesWithStudent",
              label: "Responsável 2 reside com aluno",
              example: false,
            },
            {
              category: "Família",
              key: "family.members",
              label: "Composição familiar",
              example: [
                {
                  name: "Pedro",
                  relationship: "Irmão",
                  birthDate: "2015-03-10",
                  whatsapp: "",
                  livesWithStudent: true,
                },
              ],
            },
            {
              category: "Endereço",
              key: "address.zipCode",
              label: "CEP",
              example: "01001-000",
            },
            {
              category: "Endereço",
              key: "address.street",
              label: "Rua",
              example: "Rua A",
            },
            {
              category: "Endereço",
              key: "address.number",
              label: "Número",
              example: "100",
            },
            {
              category: "Endereço",
              key: "address.complement",
              label: "Complemento",
              example: "Casa 2",
            },
            {
              category: "Endereço",
              key: "address.neighborhood",
              label: "Bairro",
              example: "Centro",
            },
            {
              category: "Endereço",
              key: "address.city",
              label: "Cidade",
              example: "São Paulo",
            },
            {
              category: "Endereço",
              key: "address.state",
              label: "Estado",
              example: "SP",
            },
            {
              category: "Endereço",
              key: "address.zone",
              label: "Zona de residência",
              example: "Urbana",
            },
            {
              category: "Endereço",
              key: "address.housingType",
              label: "Tipo de moradia",
              example: "Própria",
            },
            {
              category: "Social",
              key: "social.cras",
              label: "CRAS de referência",
              example: "CRAS Centro",
            },
            {
              category: "Social",
              key: "social.familyIncome",
              label: "Renda familiar",
              example: "R$ 2.500,00",
            },
            {
              category: "Social",
              key: "social.benefits",
              label: "Benefícios do Governo",
              example: [{ name: "Bolsa Família", value: "R$ 600,00" }],
            },
            {
              category: "Escolaridade",
              key: "education.level",
              label: "Escolaridade",
              example: "Ensino fundamental",
            },
            {
              category: "Escolaridade",
              key: "education.school",
              label: "Escola",
              example: "Escola Municipal",
            },
            {
              category: "Escolaridade",
              key: "education.grade",
              label: "Série/Ano",
              example: "7º ano",
            },
            {
              category: "Escolaridade",
              key: "education.period",
              label: "Período em que estuda",
              example: "Manhã",
            },
            {
              category: "Escolaridade",
              key: "education.extraCourses",
              label: "Cursos extras",
              example: [{ name: "Informática", institution: "Projeto Social" }],
            },
            {
              category: "Escolaridade",
              key: "education.courseHistory",
              label: "Histórico de cursos",
              example: [
                {
                  name: "Turma A",
                  startDate: "2026-02-01",
                  endDate: "",
                  status: "Cursando",
                },
              ],
            },
            {
              category: "Saúde",
              key: "health.allergy.answer",
              label: "Possui alergia?",
              example: "Sim",
            },
            {
              category: "Saúde",
              key: "health.allergy.details",
              label: "Alergia - quais?",
              example: "Dipirona",
            },
            {
              category: "Saúde",
              key: "health.healthIssue.answer",
              label: "Problema particular de saúde?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.healthIssue.details",
              label: "Problema de saúde - quais?",
              example: "Asma",
            },
            {
              category: "Saúde",
              key: "health.foodRestriction.answer",
              label: "Restrição alimentar?",
              example: "Sim",
            },
            {
              category: "Saúde",
              key: "health.foodRestriction.details",
              label: "Restrição alimentar - quais?",
              example: "Lactose",
            },
            {
              category: "Saúde",
              key: "health.controlledMedication.answer",
              label: "Medicação controlada?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.controlledMedication.details",
              label: "Medicação controlada - quais?",
              example: "Metilfenidato",
            },
            {
              category: "Saúde",
              key: "health.activitySupport.answer",
              label: "Acompanhamento em atividades?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.activitySupport.details",
              label: "Acompanhamento - detalhes",
              example: "Apoio em atividades externas",
            },
            {
              category: "Saúde",
              key: "health.physicalLimitation.answer",
              label: "Limitação física?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.physicalLimitation.details",
              label: "Limitação física - quais?",
              example: "Evitar corrida intensa",
            },
            {
              category: "Saúde",
              key: "health.psychoactiveSubstances.answer",
              label: "Uso de substâncias psicoativas?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.psychoactiveSubstances.details",
              label: "Substâncias psicoativas - detalhes",
              example: "Acompanhamento familiar",
            },
            {
              category: "Saúde",
              key: "health.disability.answer",
              label: "Possui deficiência?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.disability.details",
              label: "Deficiência - quais?",
              example: "Deficiência auditiva",
            },
            {
              category: "Outros",
              key: "customFields",
              label: "Campos personalizados",
              example: { origem: "Sistema externo" },
            },
            {
              category: "Outros",
              key: "files.categories",
              label: "Categorias de arquivos",
              example: ["Documentos pessoais"],
            },
            {
              category: "Outros",
              key: "files.documents",
              label: "Documentos/arquivos",
              example: [
                {
                  category: "Documentos pessoais",
                  name: "RG",
                  url: "https://cdn.exemplo.com/docs/rg.pdf",
                },
              ],
            },
            {
              category: "Outros",
              key: "confidential.notes",
              label: "Anotações confidenciais",
              example: "Acesso restrito à coordenação",
            },
          ]}
          payloadExample={`{
  "students": [
    {
      "fullName": "Maria da Silva",
      "cpf": "12345678900",
      "email": "maria@exemplo.com",
      "classId": 1,
      "status": "ACTIVE"
    }
  ]
}`}
        />
      ) : null}
      {automationView === "attendees-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Atendidos"
          description="Escolha os campos do cadastro de atendidos que o outro sistema deve enviar e gere o payload para colar na aplicação externa."
          icon={FaUserGraduate}
          sourceBasePath="/attendees/provisioning"
          defaultPath="/attendees/provisioning/intake"
          payloadRoot="attendees"
          fields={[
            {
              category: "Dados pessoais",
              key: "registrationNumber",
              label: "Matrícula",
              example: "2026-001",
            },
            {
              category: "Dados pessoais",
              key: "photoUrl",
              label: "Foto",
              example: "https://cdn.exemplo.com/atendidos/maria.jpg",
            },
            {
              category: "Dados pessoais",
              key: "fullName",
              label: "Nome completo",
              example: "Maria da Silva",
              required: true,
            },
            {
              category: "Dados pessoais",
              key: "socialName",
              label: "Nome social",
              example: "Maria",
            },
            {
              category: "Dados pessoais",
              key: "enrollmentDate",
              label: "Data de inscrição",
              example: "2026-02-01",
              default: true,
            },
            {
              category: "Dados pessoais",
              key: "birthDate",
              label: "Data de nascimento",
              example: "2010-05-20",
            },
            {
              category: "Dados pessoais",
              key: "gender",
              label: "Gênero",
              example: "Feminino",
            },
            {
              category: "Dados pessoais",
              key: "ethnicity",
              label: "Etnia/cor",
              example: "Parda",
            },
            {
              category: "Dados pessoais",
              key: "nationality",
              label: "Nacionalidade",
              example: "Brasileira",
            },
            {
              category: "Dados pessoais",
              key: "nis",
              label: "NIS",
              example: "12345678901",
            },
            {
              category: "Contato e documentos",
              key: "whatsapp",
              label: "WhatsApp",
              example: "(11) 99999-0000",
            },
            {
              category: "Contato e documentos",
              key: "phone",
              label: "Telefone",
              example: "(11) 3000-0000",
            },
            {
              category: "Contato e documentos",
              key: "email",
              label: "E-mail",
              example: "maria@exemplo.com",
              default: true,
            },
            {
              category: "Contato e documentos",
              key: "cpf",
              label: "CPF",
              example: "12345678900",
              required: true,
            },
            {
              category: "Contato e documentos",
              key: "rg",
              label: "RG",
              example: "12.345.678-9",
            },
            {
              category: "Grupo e status",
              key: "exitDate",
              label: "Data de saída",
              example: "2026-12-20",
            },
            {
              category: "Grupo e status",
              key: "exitReason",
              label: "Motivo de saída",
              example: "Encerramento do acompanhamento",
            },
            {
              category: "Grupo e status",
              key: "status",
              label: "Status",
              example: "ACTIVE",
              default: true,
            },
            {
              category: "Grupo e status",
              key: "classId",
              label: "ID da turma",
              example: 1,
              default: true,
            },
            {
              category: "Grupo e status",
              key: "classLinkedAt",
              label: "Data de vínculo com turma",
              example: "2026-02-01",
            },
            {
              category: "Família",
              key: "family.responsible1.name",
              label: "Responsável 1 - nome",
              example: "João da Silva",
            },
            {
              category: "Família",
              key: "family.responsible1.cpf",
              label: "Responsável 1 - CPF",
              example: "98765432100",
            },
            {
              category: "Família",
              key: "family.responsible1.whatsapp",
              label: "Responsável 1 - WhatsApp",
              example: "(11) 98888-0000",
            },
            {
              category: "Família",
              key: ["family", "responsible1", "livesWithAttendee"].join("."),
              label: "Responsável 1 reside com atendido",
              example: true,
            },
            {
              category: "Família",
              key: "family.responsible2.name",
              label: "Responsável 2 - nome",
              example: "Ana da Silva",
            },
            {
              category: "Família",
              key: "family.responsible2.cpf",
              label: "Responsável 2 - CPF",
              example: "11122233344",
            },
            {
              category: "Família",
              key: "family.responsible2.whatsapp",
              label: "Responsável 2 - WhatsApp",
              example: "(11) 97777-0000",
            },
            {
              category: "Família",
              key: ["family", "responsible2", "livesWithAttendee"].join("."),
              label: "Responsável 2 reside com atendido",
              example: false,
            },
            {
              category: "Família",
              key: "family.members",
              label: "Composição familiar",
              example: [
                {
                  name: "Pedro",
                  relationship: "Irmão",
                  birthDate: "2015-03-10",
                  whatsapp: "",
                  livesWithAttendee: true,
                },
              ],
            },
            {
              category: "Endereço",
              key: "address.zipCode",
              label: "CEP",
              example: "01001-000",
            },
            {
              category: "Endereço",
              key: "address.street",
              label: "Rua",
              example: "Rua A",
            },
            {
              category: "Endereço",
              key: "address.number",
              label: "Número",
              example: "100",
            },
            {
              category: "Endereço",
              key: "address.complement",
              label: "Complemento",
              example: "Casa 2",
            },
            {
              category: "Endereço",
              key: "address.neighborhood",
              label: "Bairro",
              example: "Centro",
            },
            {
              category: "Endereço",
              key: "address.city",
              label: "Cidade",
              example: "São Paulo",
            },
            {
              category: "Endereço",
              key: "address.state",
              label: "Estado",
              example: "SP",
            },
            {
              category: "Endereço",
              key: "address.zone",
              label: "Zona de residência",
              example: "Urbana",
            },
            {
              category: "Endereço",
              key: "address.housingType",
              label: "Tipo de moradia",
              example: "Própria",
            },
            {
              category: "Social",
              key: "social.cras",
              label: "CRAS de referência",
              example: "CRAS Centro",
            },
            {
              category: "Social",
              key: "social.familyIncome",
              label: "Renda familiar",
              example: "R$ 2.500,00",
            },
            {
              category: "Social",
              key: "social.benefits",
              label: "Benefícios do Governo",
              example: [{ name: "Bolsa Família", value: "R$ 600,00" }],
            },
            {
              category: "Escolaridade",
              key: "education.level",
              label: "Escolaridade",
              example: "Ensino fundamental",
            },
            {
              category: "Escolaridade",
              key: "education.school",
              label: "Escola",
              example: "Escola Municipal",
            },
            {
              category: "Escolaridade",
              key: "education.grade",
              label: "Série/Ano",
              example: "7º ano",
            },
            {
              category: "Escolaridade",
              key: "education.period",
              label: "Período em que estuda",
              example: "Manhã",
            },
            {
              category: "Escolaridade",
              key: "education.extraCourses",
              label: "Cursos extras",
              example: [{ name: "Informática", institution: "Projeto Social" }],
            },
            {
              category: "Grupo e status",
              key: "education.groupHistory",
              label: "Histórico de grupos",
              example: [
                {
                  name: "Grupo de Convivência",
                  className: "Turma A",
                  startDate: "2026-02-01",
                  endDate: "",
                  status: "Participando",
                },
              ],
            },
            {
              category: "Saúde",
              key: "health.allergy.answer",
              label: "Possui alergia?",
              example: "Sim",
            },
            {
              category: "Saúde",
              key: "health.allergy.details",
              label: "Alergia - quais?",
              example: "Dipirona",
            },
            {
              category: "Saúde",
              key: "health.healthIssue.answer",
              label: "Problema particular de saúde?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.healthIssue.details",
              label: "Problema de saúde - quais?",
              example: "Asma",
            },
            {
              category: "Saúde",
              key: "health.foodRestriction.answer",
              label: "Restrição alimentar?",
              example: "Sim",
            },
            {
              category: "Saúde",
              key: "health.foodRestriction.details",
              label: "Restrição alimentar - quais?",
              example: "Lactose",
            },
            {
              category: "Saúde",
              key: "health.controlledMedication.answer",
              label: "Medicação controlada?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.controlledMedication.details",
              label: "Medicação controlada - quais?",
              example: "Metilfenidato",
            },
            {
              category: "Saúde",
              key: "health.activitySupport.answer",
              label: "Acompanhamento em atividades?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.activitySupport.details",
              label: "Acompanhamento - detalhes",
              example: "Apoio em atividades externas",
            },
            {
              category: "Saúde",
              key: "health.physicalLimitation.answer",
              label: "Limitação física?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.physicalLimitation.details",
              label: "Limitação física - quais?",
              example: "Evitar corrida intensa",
            },
            {
              category: "Saúde",
              key: "health.psychoactiveSubstances.answer",
              label: "Uso de substâncias psicoativas?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.psychoactiveSubstances.details",
              label: "Substâncias psicoativas - detalhes",
              example: "Acompanhamento familiar",
            },
            {
              category: "Saúde",
              key: "health.disability.answer",
              label: "Possui deficiência?",
              example: "Não",
            },
            {
              category: "Saúde",
              key: "health.disability.details",
              label: "Deficiência - quais?",
              example: "Deficiência auditiva",
            },
            {
              category: "Outros",
              key: "customFields",
              label: "Campos personalizados",
              example: { origem: "Sistema externo" },
            },
            {
              category: "Outros",
              key: "files.categories",
              label: "Categorias de arquivos",
              example: ["Documentos pessoais"],
            },
            {
              category: "Outros",
              key: "files.documents",
              label: "Documentos/arquivos",
              example: [
                {
                  category: "Documentos pessoais",
                  name: "RG",
                  url: "https://cdn.exemplo.com/docs/rg.pdf",
                },
              ],
            },
            {
              category: "Outros",
              key: "confidential.notes",
              label: "Anotações confidenciais",
              example: "Acesso restrito à coordenação",
            },
          ]}
          payloadExample={`{
  "attendees": [
    {
      "fullName": "Maria da Silva",
      "cpf": "12345678900",
      "email": "maria@exemplo.com",
      "classId": 1,
      "status": "ACTIVE"
    }
  ]
}`}
        />
      ) : null}
      {automationView === "student-classes-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Turmas"
          description="Configure a fonte externa e selecione os campos das turmas que serão enviados por payload."
          icon={FaUsers}
          sourceBasePath="/student-classes/provisioning"
          defaultPath="/student-classes/provisioning/intake"
          payloadRoot="classes"
          fields={[
            {
              key: "name",
              label: "Nome da turma",
              example: "Turma A - Manhã",
              required: true,
            },
            {
              key: "courseId",
              label: "ID do curso",
              example: 1,
              required: true,
            },
            {
              key: "period",
              label: "Período",
              example: "Manhã",
              default: true,
            },
            {
              key: "teacherIds",
              label: "IDs dos professores",
              example: [2, 3],
            },
            {
              key: "status",
              label: "Status",
              example: "PLANNED",
              default: true,
            },
            {
              key: "startDate",
              label: "Data de início",
              example: "2026-02-01",
            },
            { key: "endDate", label: "Data de término", example: "2026-12-15" },
            {
              key: "notes",
              label: "Observações",
              example: "Turma importada via integração",
            },
          ]}
          payloadExample={`{
  "classes": [
    {
      "name": "Turma A - Manhã",
      "courseId": 1,
      "period": "Manhã",
      "status": "PLANNED"
    }
  ]
}`}
        />
      ) : null}
      {automationView === "attendee-classes-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Turmas de Atendidos"
          description="Configure a fonte externa e selecione os campos das turmas que serão enviados por payload."
          icon={FaUsers}
          sourceBasePath="/attendee-classes/provisioning"
          defaultPath="/attendee-classes/provisioning/intake"
          payloadRoot="classes"
          fields={[
            {
              key: "name",
              label: "Nome da turma",
              example: "Turma A - Manhã",
              required: true,
            },
            {
              key: "groupId",
              label: "ID do grupo",
              example: 1,
              required: true,
            },
            {
              key: "period",
              label: "Período",
              example: "Manhã",
              default: true,
            },
            {
              key: "teacherIds",
              label: "IDs dos professores",
              example: [2, 3],
            },
            {
              key: "status",
              label: "Status",
              example: "PLANNED",
              default: true,
            },
            {
              key: "startDate",
              label: "Data de início",
              example: "2026-02-01",
            },
            { key: "endDate", label: "Data de término", example: "2026-12-15" },
            {
              key: "notes",
              label: "Observações",
              example: "Turma importada via integração",
            },
          ]}
          payloadExample={`{
  "classes": [
    {
      "name": "Turma A - Manhã",
      "groupId": 1,
      "period": "Manhã",
      "status": "PLANNED"
    }
  ]
}`}
        />
      ) : null}
      {automationView === "student-courses-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Cursos"
          description="Defina quais campos de cursos serão preenchidos pelo sistema externo e copie o payload gerado."
          icon={FaCogs}
          sourceBasePath="/student-courses/provisioning"
          defaultPath="/student-courses/provisioning/intake"
          payloadRoot="courses"
          fields={[
            {
              key: "name",
              label: "Nome do curso",
              example: "Programação Web",
              required: true,
            },
            {
              key: "coordinator",
              label: "Coordenador",
              example: "Ana Souza",
              default: true,
            },
            {
              key: "status",
              label: "Status",
              example: "ACTIVE",
              default: true,
            },
            {
              key: "notes",
              label: "Observações",
              example: "Curso importado via integração",
            },
          ]}
          payloadExample={`{
  "courses": [
    {
      "name": "Programação Web",
      "coordinator": "Ana Souza",
      "status": "ACTIVE"
    }
  ]
}`}
        />
      ) : null}

      {automationView === "attendee-groups-provisioning" ? (
        <ResourceProvisioningPanel
          title="Integração de Grupos de Atendidos"
          description="Defina quais campos de grupos serão preenchidos pelo sistema externo e copie o payload gerado."
          icon={FaCogs}
          sourceBasePath="/attendee-groups/provisioning"
          defaultPath="/attendee-groups/provisioning/intake"
          payloadRoot="groups"
          fields={[
            {
              key: "name",
              label: "Nome do grupo",
              example: "Grupo de Convivência",
              required: true,
            },
            {
              key: "coordinator",
              label: "Coordenador",
              example: "Ana Souza",
              default: true,
            },
            {
              key: "status",
              label: "Status",
              example: "ACTIVE",
              default: true,
            },
            {
              key: "notes",
              label: "Observações",
              example: "Grupo importado via integração",
            },
          ]}
          payloadExample={`{
  "groups": [
    {
      "name": "Grupo de Convivência",
      "coordinator": "Ana Souza",
      "status": "ACTIVE"
    }
  ]
}`}
        />
      ) : null}

      {automationView === "nash-chatgpt" && canManageNashAiSettings ? (
        <section className="rounded-3xl border border-indigo-200/70 bg-white/95 p-6 shadow-xl">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
                <FaKey className="text-indigo-600" /> Integração ChatGPT da Nash
                IA
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Configure uma chave separada para as respostas conversacionais
                da Nash IA no Dashboard e no Nash Chat. Esta configuração é
                independente do ChatGPT usado em Projetos e Estoque.
              </p>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${nashAiSettings.nashOpenAiApiKeyConfigured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
            >
              {nashAiSettings.nashOpenAiApiKeyConfigured
                ? `Chave configurada ${nashAiSettings.nashOpenAiApiKeySource === "environment" ? "no servidor" : "na plataforma"}`
                : "Chave não configurada"}
            </span>
          </div>
          {nashAiSettingsMessage ? (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
              {nashAiSettingsMessage}
            </div>
          ) : null}
          {nashAiSettingsError ? (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {nashAiSettingsError}
            </div>
          ) : null}
          {nashAiSettingsLoading ? (
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-600">
              <FaSync className="animate-spin" /> Carregando configuração...
            </div>
          ) : (
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={saveNashAiIntegrationSettings}
            >
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                API key da OpenAI para Nash IA
                <input
                  type="password"
                  value={nashAiSettings.nashOpenAiApiKey || ""}
                  onChange={(event) =>
                    updateNashAiSetting("nashOpenAiApiKey", event.target.value)
                  }
                  placeholder={
                    nashAiSettings.nashOpenAiApiKeyConfigured
                      ? "Digite uma nova chave para substituir a atual"
                      : "sk-..."
                  }
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                <span className="text-xs font-medium text-slate-500">
                  A chave salva não é exibida novamente por segurança. Deixe em
                  branco para manter a atual.
                </span>
              </label>
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Modelo da OpenAI para Nash IA
                <select
                  value={nashAiSettings.nashOpenAiModel || "gpt-5.4-mini"}
                  onChange={(event) =>
                    updateNashAiSetting("nashOpenAiModel", event.target.value)
                  }
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="gpt-5.4-mini">
                    gpt-5.4-mini (recomendado)
                  </option>
                  <option value="gpt-5-mini">gpt-5-mini</option>
                  <option value="gpt-5">gpt-5 (mais forte)</option>
                  <option value="gpt-4o">gpt-4o</option>
                  <option value="gpt-4o-mini">gpt-4o-mini</option>
                </select>
                <span className="text-xs font-medium text-slate-500">
                  Use gpt-5.4-mini, gpt-5 ou gpt-5-mini para respostas mais
                  precisas e completas.
                </span>
              </label>
              <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700 md:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(nashAiSettings.clearNashOpenAiApiKey)}
                  disabled={
                    nashAiSettings.nashOpenAiApiKeySource === "environment"
                  }
                  onChange={(event) =>
                    updateNashAiSetting(
                      "clearNashOpenAiApiKey",
                      event.target.checked,
                    )
                  }
                />
                Remover chave da Nash IA salva na plataforma
              </label>
              <div className="flex justify-end md:col-span-2">
                <button
                  type="submit"
                  disabled={nashAiSettingsSaving}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-70"
                >
                  {nashAiSettingsSaving ? (
                    <FaSync className="animate-spin" />
                  ) : (
                    <FaKey />
                  )}{" "}
                  {nashAiSettingsSaving
                    ? "Salvando..."
                    : "Salvar integração da Nash IA"}
                </button>
              </div>
            </form>
          )}
        </section>
      ) : null}

      {automationView === "project-bank-ai" ? (
        <section className="rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
                <FaKey className="text-blue-600" /> Integração ChatGPT para
                Projetos e Estoque
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Configure aqui a chave da OpenAI usada pelo botão “Escrever com
                IA” no Banco de Projetos e pelo relatório com análise de IA no
                Controle de Estoque. Sem essa chave, o Banco de Projetos tenta
                usar a IA gratuita de fallback e, por último, um modelo local de
                contingência; o Estoque libera a análise somente com ChatGPT
                configurado.
              </p>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${aiSettings.openAiApiKeyConfigured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
            >
              {aiSettings.openAiApiKeyConfigured
                ? `Chave configurada ${aiSettings.openAiApiKeySource === "environment" ? "no servidor" : "na plataforma"}`
                : "Chave não configurada"}
            </span>
          </div>
          {aiSettingsMessage ? (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
              {aiSettingsMessage}
            </div>
          ) : null}
          {aiSettingsError ? (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {aiSettingsError}
            </div>
          ) : null}
          {aiSettingsLoading ? (
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-600">
              <FaSync className="animate-spin" /> Carregando configuração...
            </div>
          ) : (
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={saveAiIntegrationSettings}
            >
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                API key da OpenAI
                <input
                  type="password"
                  value={aiSettings.openAiApiKey || ""}
                  onChange={(event) =>
                    updateAiSetting("openAiApiKey", event.target.value)
                  }
                  placeholder={
                    aiSettings.openAiApiKeyConfigured
                      ? "Digite uma nova chave para substituir a atual"
                      : "sk-..."
                  }
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                <span className="text-xs font-medium text-slate-500">
                  A chave salva não é exibida novamente por segurança. Deixe em
                  branco para manter a atual.
                </span>
              </label>
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Modelo da OpenAI
                <select
                  value={aiSettings.openAiModel || "gpt-5.4-mini"}
                  onChange={(event) =>
                    updateAiSetting("openAiModel", event.target.value)
                  }
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="gpt-5.4-mini">
                    gpt-5.4-mini (recomendado)
                  </option>
                  <option value="gpt-5-mini">gpt-5-mini</option>
                  <option value="gpt-5">gpt-5 (mais forte)</option>
                  <option value="gpt-4o">gpt-4o</option>
                  <option value="gpt-4o-mini">gpt-4o-mini</option>
                </select>
                <span className="text-xs font-medium text-slate-500">
                  Modelos GPT mais fortes aumentam a precisão; use gpt-5.4-mini
                  ou gpt-5 quando sua conta permitir.
                </span>
              </label>
              <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700 md:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(aiSettings.clearOpenAiApiKey)}
                  disabled={aiSettings.openAiApiKeySource === "environment"}
                  onChange={(event) =>
                    updateAiSetting("clearOpenAiApiKey", event.target.checked)
                  }
                />
                Remover chave salva na plataforma
              </label>
              <div className="flex justify-end md:col-span-2">
                <button
                  type="submit"
                  disabled={aiSettingsSaving}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-70"
                >
                  {aiSettingsSaving ? (
                    <FaSync className="animate-spin" />
                  ) : (
                    <FaKey />
                  )}{" "}
                  {aiSettingsSaving ? "Salvando..." : "Salvar integração"}
                </button>
              </div>
            </form>
          )}
        </section>
      ) : null}

      {automationView === "process" ? (
        <>
          <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
            <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-slate-900">
                  Processos e automações
                </h2>
                <InfoTooltip label="Busque um processo e ajuste integrações rapidamente." />
              </div>
              <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
                <div className="relative md:w-72">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar por nome ou categoria"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    aria-label="Buscar processos"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="all">Todos os processos</option>
                  <option value="with">Somente com automações</option>
                  <option value="without">Somente sem automações</option>
                </select>
              </div>
            </header>

            {loading ? (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                <FaSync className="animate-spin" /> Carregando panorama de
                automações...
              </div>
            ) : error ? (
              <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <FaExclamationTriangle /> {error}
              </div>
            ) : filteredProcesses.length ? (
              <div className="grid gap-4 xl:grid-cols-2">
                {filteredProcesses.map((process) => {
                  const hasAutomations = process.totalIntegrations > 0;
                  const Icon = hasAutomations ? FaCheckCircle : FaPlug;
                  return (
                    <article
                      key={process.processId}
                      className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-md transition hover:-translate-y-0.5 hover:shadow-xl"
                    >
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">
                              {process.processName ||
                                `Processo #${process.processId}`}
                            </h3>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                <Icon
                                  className={
                                    hasAutomations
                                      ? "text-emerald-500"
                                      : "text-slate-400"
                                  }
                                />
                                {hasAutomations
                                  ? "Automação configurada"
                                  : "Sem automação"}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                Versão {process.version}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                {process.status === "published"
                                  ? "Publicado"
                                  : "Rascunho"}
                              </span>
                              {process.category ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                                  {process.category}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-center text-xs text-slate-500">
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
                            <p className="text-xs uppercase tracking-wider text-emerald-700">
                              Ativas
                            </p>
                            <p className="mt-1 text-lg font-semibold text-emerald-700">
                              {process.activeIntegrations}
                            </p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-wider text-slate-600">
                              Total
                            </p>
                            <p className="mt-1 text-lg font-semibold text-slate-700">
                              {process.totalIntegrations}
                            </p>
                          </div>
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs uppercase tracking-wider text-amber-700">
                              Pendentes
                            </p>
                            <p className="mt-1 text-lg font-semibold text-amber-700">
                              {process.inactiveIntegrations}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                          Última atualização das integrações:{" "}
                          {formatDateTime(process.lastIntegrationUpdate)}
                        </div>
                      </div>

                      <div className="mt-4 space-y-3">
                        <button
                          type="button"
                          onClick={() => openSettings(process)}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
                        >
                          Abrir configurações{" "}
                          <FaArrowRight className="text-xs" />
                        </button>
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                            Templates recomendados
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {automationTemplates.map((templateDef) => {
                              const TemplateIcon = templateDef.icon;
                              return (
                                <button
                                  key={`${process.processId}-${templateDef.id}`}
                                  type="button"
                                  onClick={() =>
                                    openSettings(process, templateDef)
                                  }
                                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm transition hover:shadow ${templateDef.accent}`}
                                >
                                  <TemplateIcon /> {templateDef.shortLabel}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center text-sm text-slate-500">
                Nenhum processo encontrado com os filtros selecionados.
              </div>
            )}
          </section>

          <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
            <header className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-900">
                Templates de automação
              </h2>
              <InfoTooltip label="Cada template já sugere método, headers e eventos principais." />
            </header>
            <div className="grid gap-4 md:grid-cols-3">
              {automationTemplates.map((templateDef) => {
                const TemplateIcon = templateDef.icon;
                return (
                  <div
                    key={templateDef.id}
                    className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-md"
                  >
                    <div className="space-y-3">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${templateDef.accent}`}
                      >
                        <TemplateIcon /> {templateDef.shortLabel}
                      </div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {templateDef.title}
                        </h3>
                        <InfoTooltip label={templateDef.description} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                      <span>Detalhes</span>
                      <InfoTooltip
                        label={`Método ${templateDef.template.method} · Timeout ${templateDef.template.timeoutMs / 1000}s · Eventos ${templateDef.template.events.join(", ")}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
            <header className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-900">
                Boas práticas rápidas
              </h2>
              <InfoTooltip label="Passe o cursor nas dicas para ver instruções objetivas." />
            </header>
            <div className="flex flex-wrap gap-2">
              {automationTips.map((tip) => (
                <span
                  key={tip.id}
                  className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600"
                  title={tip.description}
                >
                  {tip.label}
                </span>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

